import React, { FC, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { FormMode } from 'src/app/common/types';
import { LayoutSplashScreen } from 'src/app/layout';
import { PROSPECT_REFERRAL_BASE_PATH, PROSPECT_REFERRAL_DEFAULT_CAMPAIGN_ID } from '../../constants';
import { EPublishStatus } from '../../enum/prospect-referral.enum';
import { getProspectReferral, updateProspectReferral } from '../../network/prospectReferralCrud';
import { ProspectReferralFormState } from '../../types/prospect-referral-types';
import ProspectReferralDetailForm from './components/ProspectReferralDetailForm';

const initialState: ProspectReferralFormState = {
  title: '',
  bannerImg: null,
  introduction: '',
  additionalInformation: '',
  tabs: [
    {
      tabTitle: '',
      tabDescription: '',
    },
  ],
  referralForm: {
    isName: true,
    isPhone: true,
    isProvinceCity: false,
    isDob: false,
    isEmail: false,
  },
  leadDistributionRule: {
    campaignId: PROSPECT_REFERRAL_DEFAULT_CAMPAIGN_ID,
  },
  agentSegment: {
    isTraditionalAgents: false,
    isPruventure: false,
    isGroupOfUsers: false,
    userFile: null,
    userList: [],
  },
  status: EPublishStatus.UNPUBLISHED,
  webflowLink: '',
};

const ProspectReferralDetailPage: FC<RouteComponentProps> = ({ history, location }) => {
  const dispatch = useDispatch();

  const formMode = useMemo(
    () => (location.pathname.includes('edit') ? FormMode.EDIT : FormMode.VIEW),
    [location.pathname],
  );

  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [initData, setInitData] = useState<ProspectReferralFormState>({ ...initialState });
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);

  const initLoad = React.useCallback(async () => {
    try {
      setFormLoading(true);
      const result = await getProspectReferral(dispatch);
      if (result) {
        setInitData({ ...initialState, ...result });
      }
    } catch (err) {
      // No record yet — keep default initialState
    } finally {
      setFormLoading(false);
      setPageLoaded(true);
    }
  }, [dispatch]);

  React.useEffect(() => {
    initLoad();
  }, []);

  const onSubmit = async (formData: ProspectReferralFormState, submitStatus: string) => {
    try {
      setFormLoading(true);
      await updateProspectReferral({ ...formData, status: submitStatus }, dispatch);
      await initLoad();
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: submitStatus === EPublishStatus.PUBLISHED ? 'Published successfully' : 'Saved successfully',
          },
        ]),
      );
      history.push(`${PROSPECT_REFERRAL_BASE_PATH}/detail`);
    } catch (err: any) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: err?.message || 'An error occurred. Please try again.',
          },
        ]),
      );
    } finally {
      setFormLoading(false);
    }
  };

  // if (formLoading && !pageLoaded) {
  //   return <LayoutSplashScreen />;
  // }

  return (
    <ProspectReferralDetailForm
      key={pageLoaded ? 'loaded' : 'initial'}
      initialValues={initData}
      onSubmit={onSubmit}
      history={history}
      formMode={formMode}
      formLoading={formLoading}
      disabled={formMode === FormMode.VIEW}
    />
  );
};

export default ProspectReferralDetailPage;
